import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DatasourceFileResponseIntermediate, IFileDataSourceApiService } from 'interfaces-lib';
import { jwtDecode } from 'jwt-decode';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { IdentityStorage } from 'storage-service-lib';
import { BullhornFileReaderService, IAttachmentFile } from './bullhorn.filereader.service';

@Injectable({
  providedIn: 'root',
})
export class BullhornFileDataSourceApiService implements IFileDataSourceApiService {
  private fileService: BullhornFileReaderService;

  constructor(
    private http: HttpClient,
    private identityStorage: IdentityStorage,
  ) {
    this.fileService = new BullhornFileReaderService();
  }

  postFile(file: File, fileParams: any): Observable<any> {
    // post to update a file in Bh, Put to add a new one
    return this.fileService.getFileContents(file).pipe(switchMap((attachmentFile) => this.putFile(attachmentFile, fileParams)));
  }

  checkFile(id: string, fileParams: any): Observable<boolean> {
    BullhornFileDataSourceApiService.assertFileParams(fileParams);

    const decoded = jwtDecode<any>(this.identityStorage.get()?.token as string);
    const url = `${decoded.restUrl}/file/${fileParams.entity.type}/${fileParams.entity.id}/${id}`;
    return this.http.get<any>(url).pipe(
      tap((resp) => console.log('Getting file ', resp)),
      map((resp) => true),
      catchError((err) => {
        if (err.status === 404) {
          return of(false);
        }
        throw err;
      }),
    );
  }

  getFileData(fileDatasourcePath: string[]): Observable<DatasourceFileResponseIntermediate> {
    const [_, entityType, entityId, fileId] = fileDatasourcePath;
    const decoded = jwtDecode<any>((this.identityStorage.get()?.token as string));
    const fileIdParam = fileId ? `/${fileId}` : '';
    const url = `${decoded.restUrl}/file/${entityType}/${entityId}${fileIdParam}`;
    return this.http.get(url).pipe(
      map((response: any) => {
        return {
          contentType: response.File.contentType,
          base64EncodedContent: response.File.fileContent,
          name: response.File.name,
        };
      }),
    );
  }

  private putFile(attachmentFile: IAttachmentFile, fileParams: any): Observable<any> {
    const decoded = jwtDecode<any>(this.identityStorage.get()?.token as string);
    const url = `${decoded.restUrl}/file/${fileParams.entity.type}/${fileParams.entity.id}`;

    const body = {
      externalID: 'file', // cannot be undefined
      fileContent: attachmentFile.fileContent,
      fileType: 'SAMPLE',
      name: attachmentFile.name,
      contentType: attachmentFile.contentType,
      description: 'File uploaded from Kyloe AwesomeDocs',
      type: '', // bullhorn file type (needs to be one from the file type list or will show blank)
    };

    return this.http.put<any>(url, body).pipe(
      tap((resp) => console.log('Putting file ', resp)),
      map((resp) => {
        return {
          id: resp,
          type: fileParams.fileType,
          name: attachmentFile.name,
        };
      }),
      catchError((err) => {
        if (err.status === 404) {
          return of(false);
        }
        throw err;
      }),
    );
  }

  private static assertFileParams(fileParams?: any): void {
    if (!fileParams) {
      throw new Error('No file parameters supplied for Bullhorn checkFile(..).');
    }
  }
}
